import { apiClient, tenantHub, unwrap } from './client';
import type {
  ApiEnvelope,
  OversightAlertList,
  OversightRollup,
  SpokeOversightDetail,
} from './types';

/**
 * B2 — hub-oversight console (F-TA-02, the CC moat surface). RE-grade roll-up across a hub's spokes,
 * per-spoke drill-down, and a filterable alerts feed. NEW cross-track endpoints (Track A builds them
 * in parallel); the SPA degrades to a teaching empty state where they 404. Reads only.
 */

const enc = (s: string) => encodeURIComponent(s);
const base = () => `/v1/oversight/${enc(tenantHub())}`;

/**
 * GET /v1/oversight/{hub}/rollup — per-spoke tiles + totals. Guard the arrays on unwrap, but NEVER
 * coerce a null aggregate to 0: null means "no source wired" (the backend's honesty contract) and
 * the UI renders it as "—". The `sources` map (which source fed each field, or why it is null)
 * rides through untouched.
 */
export async function getOversightRollup(): Promise<OversightRollup> {
  const { data } = await apiClient.get<ApiEnvelope<OversightRollup>>(`${base()}/rollup`);
  const body = unwrap(data);
  return {
    hub: body?.hub ?? tenantHub(),
    spokes: Array.isArray(body?.spokes) ? body.spokes : [],
    totals: body?.totals ?? {
      entityCount: null,
      openCaseCount: null,
      slaBreachCount: null,
      unresolvedSignalCount: null,
      lastReviewAt: null,
      reconciliationBreakCount: null,
    },
    generatedAt: body?.generatedAt ?? '',
    sources: body?.sources && typeof body.sources === 'object' ? body.sources : undefined,
  };
}

/** GET /v1/oversight/{hub}/spokes/{spokeId} — case breakdown, SLA breaches, recon breaks, reviews. */
export async function getSpokeOversight(spokeId: string): Promise<SpokeOversightDetail> {
  const { data } = await apiClient.get<ApiEnvelope<SpokeOversightDetail>>(
    `${base()}/spokes/${enc(spokeId)}`,
  );
  const body = unwrap(data);
  return {
    spokeId: body?.spokeId ?? spokeId,
    // null = entity graph not provisioned — surfaced honestly, never a fabricated 0.
    entityCount: body?.entityCount ?? null,
    cases: { open: body?.cases?.open ?? 0, byStatus: body?.cases?.byStatus ?? {} },
    slaBreaches: Array.isArray(body?.slaBreaches) ? body.slaBreaches : [],
    reconciliationBreaks: Array.isArray(body?.reconciliationBreaks) ? body.reconciliationBreaks : [],
    recentReviews: Array.isArray(body?.recentReviews) ? body.recentReviews : [],
    sources: body?.sources && typeof body.sources === 'object' ? body.sources : undefined,
  };
}

export interface OversightAlertQuery {
  type?: string;
  spokeId?: string;
}

/** GET /v1/oversight/{hub}/alerts?type=&spokeId= — severity-coloured alerts. Guard the array. */
export async function listOversightAlerts(q: OversightAlertQuery = {}): Promise<OversightAlertList> {
  const { data } = await apiClient.get<ApiEnvelope<OversightAlertList>>(`${base()}/alerts`, {
    params: { type: q.type, spokeId: q.spokeId },
  });
  const body = unwrap(data);
  return {
    alerts: Array.isArray(body?.alerts) ? body.alerts : [],
    totalCount: body?.totalCount ?? 0,
  };
}
